import React, {useCallback, useEffect, useState} from 'react'

import useSWR from 'swr'
import styled from 'styled-components'
import {Container, media} from 'styled-bootstrap-grid'

import {recommendations} from '@festi/common/api/recommend'
import {ProductDetail, Variant} from '@festi/common/api/rest'
import {useEvents} from '@festi/common/contexts'
import {Inventory, Property} from '@festi/common/utils/products'
import {useSingleUserPrice} from '@festi/common/utils/rest'
import useTagmanager from '@festi/common/utils/tagmanager'
import {styledTheme} from '@festi/common/themes'
import ProductImageGallery from 'src/components/common/redesign/ProductImageGallery/NewProductImageGallery'

import ProductInfo from './NewProductInfo'
import ProductTitle from './ProductTitle'
import EnergyLabel from '../../EnergyLabel'
import EnergyImageModal from '../../EnergyImageModal'
import ProductBoughtTogether from './ProductBoughtTogether'
import ProductRelated from './NewProductRelated'
import ItemBanners from '../ItemBanners'
import ProductAttributes from './ProductAttributes'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${media.md`
    padding-top: 20px;
    gap: 20px;
  `}
`

const ShowOnlyInMobile = styled.div`
  display: block;

  ${media.md`
    display: none;
  `}
`

const ShowOnlyInDesktop = styled.div`
  display: none;

  ${media.md`
    display: block;
  `}
`

const TopSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  ${media.md`
    display: grid;
    grid-template-columns: auto 476px;
    gap: 76px;
  `}

  ${media.lg`
    display: grid;
    grid-template-columns: auto 528px;
    gap: 152px;
  `}
`

const ImageGalleryContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`

const EnergyWrapper = styled.div`
  position: absolute;
  top: -20px;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  z-index: 2;

  ${media.md`
    top: -8px;
  `}
`

const EnergyLabelImageButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${styledTheme.palette.lightBlue};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const TextButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: ${styledTheme.palette.ui50Solid};
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${styledTheme.palette.lightBlue};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

interface Props {
  sku: string
  variant: Variant
  product: ProductDetail
  inventory: Inventory[]
  properties: Property[]
}

export default function ProductVariantPage({
  sku,
  variant,
  product,
  inventory,
  properties,
}: Props) {
  const {algoliaInit, userToken, sendViewEvent} = useEvents()
  const {sendEcommerceItemViewEvent} = useTagmanager()

  const [showEnergyImageModal, setShowEnergyImageModal] =
    useState<boolean>(false)
  const [showProductPropertiesModal, setShowProductPropertiesModal] =
    useState<boolean>(false)
  const [scrollToTab, setScrollToTab] = useState<number | null>(null)

  const {data: userPrice} = useSingleUserPrice(variant?.sku)

  const {data: recommended} = useSWR(
    [variant?.sku, userToken, 'recommended'],
    () => recommendations(variant?.sku, userToken),
  )

  const boughtTogether = recommended?.[0]
  const related = recommended?.[1]

  const {energyLabelImages} = variant
  const {attributes} = product

  const hasEnergyLabel =
    !!attributes['energyclass'] || !!attributes['orkuflokkur']

  const onToggleEnergyImageModal = useCallback(() => {
    setShowEnergyImageModal(!showEnergyImageModal)
  }, [showEnergyImageModal])

  useEffect(() => {
    sendEcommerceItemViewEvent(variant, product)
  }, [variant, product, sendEcommerceItemViewEvent])

  useEffect(() => {
    sendViewEvent('viewedObjectIDs', 'Product Viewed', sku)
  }, [sku, algoliaInit, sendViewEvent])

  return (
    <>
      <EnergyImageModal
        image={energyLabelImages[0]?.image?.productGallery2x}
        isOpen={showEnergyImageModal}
        onRequestClose={onToggleEnergyImageModal}
      />

      <Container>
        <Wrapper>
          <TopSectionWrapper>
            <ShowOnlyInMobile>
              <ProductTitle name={variant.name} sku={sku} />
            </ShowOnlyInMobile>

            <ImageGalleryContainer>
              {/* TODO: Add new EnergyLabel component when ready*/}
              <EnergyWrapper>
                {hasEnergyLabel && (
                  <>
                    <ShowOnlyInDesktop>
                      <TextButton
                        onClick={() => setScrollToTab(1)}
                        aria-label="Vöruupplýsingablað"
                      >
                        Vöruupplýsingablað
                      </TextButton>
                    </ShowOnlyInDesktop>
                    <ShowOnlyInMobile>
                      <TextButton
                        onClick={() => setShowProductPropertiesModal(true)}
                        aria-label="Vöruupplýsingablað"
                      >
                        Vöruupplýsingablað
                      </TextButton>
                    </ShowOnlyInMobile>
                  </>
                )}

                {!!energyLabelImages.length ? (
                  <EnergyLabelImageButton onClick={onToggleEnergyImageModal}>
                    <EnergyLabel
                      aria-label="Orkuflokkur"
                      energyClass={attributes['energyclass']}
                      oldEnergyClass={attributes['orkuflokkur']}
                    />
                  </EnergyLabelImageButton>
                ) : (
                  <EnergyLabel
                    aria-label="Orkuflokkur"
                    energyClass={attributes['energyclass']}
                    oldEnergyClass={attributes['orkuflokkur']}
                  />
                )}
              </EnergyWrapper>

              <ProductImageGallery
                sku={sku}
                variant={variant}
                product={product}
              />

              <ItemBanners
                variant={variant}
                userPrice={userPrice}
                hasEnergyLabel={hasEnergyLabel}
                onProductPage
              />
            </ImageGalleryContainer>

            <ProductInfo
              product={product}
              variant={variant}
              inventory={inventory}
              userPrice={userPrice}
            />
          </TopSectionWrapper>

          <ProductAttributes
            properties={properties}
            product={product}
            showProductPropertiesModal={showProductPropertiesModal}
            setShowProductPropertiesModal={setShowProductPropertiesModal}
            scrollToTab={scrollToTab}
            setScrollToTab={setScrollToTab}
          />

          <ProductBoughtTogether variants={boughtTogether} />
          {/* TODO: Add the comparison table post launch - until then this will look the same as ProductBoughtTogether */}
          <ProductRelated variants={related} />
        </Wrapper>
      </Container>
    </>
  )
}
